import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Divider,
  Dropdown,
} from '@makeably/creativex-design-system';
import ItemsTable from 'components/molecules/ItemsTable';
import { addToast } from 'components/organisms/Toasts';
import {
  getBinsByTag,
  getBinItems,
} from 'components/reporting/binItems';
import BreakdownDrawer from 'components/reporting/BreakdownDrawer';
import ConfigureReport from 'components/reporting/ConfigureReport';
import { getCustomData } from 'components/reporting/dataFetcher';
import MetricVisualization from 'components/reporting/MetricVisualization';
import ReportDatePicker from 'components/reporting/ReportDatePicker';
import ReportingFilter from 'components/reporting/ReportingFilter';
import ReportTags from 'components/reporting/ReportTags';
import { groupRecords } from 'components/reporting/scoreDataFetcher';
import {
  attributeLookupsProps,
  columnCounts,
  customFilterProps,
  dateOptionProps,
  initialDateRange,
  mixpanelDateChange,
  propertiesProps,
  scoreProps,
  thirdPartyScoreProps,
  visualizationTypes,
} from 'components/reporting/shared';
import {
  calcPropertiesJson,
  getHeaders,
  getMetrics,
  getScoresSegments,
  parseProperties,
  preprocessRecords,
  setTableStyles,
  updateChartMetrics,
} from 'components/reporting/utilities';
import { saveItemsCsvFile } from 'utilities/file';
import { getObjFilterTest } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import { track } from 'utilities/mixpanel';
import { removeProperty } from 'utilities/object';
import { getAuthenticityToken } from 'utilities/requests';
import {
  scoreGroupingDimensionsReportingReportsPath,
  scoreRecordsReportingReportsPath,
  editReportingReportPath,
} from 'utilities/routes';
import styles from './ScoreReport.module.css';

const propTypes = {
  attributeLookups: attributeLookupsProps.isRequired,
  canViewSpend: PropTypes.bool.isRequired,
  customFilters: PropTypes.arrayOf(customFilterProps).isRequired,
  customRangeProps: PropTypes.shape({
    customDatesEnabled: PropTypes.bool,
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }).isRequired,
  customSegments: PropTypes.arrayOf(PropTypes.string).isRequired,
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  initialDescription: PropTypes.string.isRequired,
  initialProperties: propertiesProps.isRequired,
  initialTitle: PropTypes.string.isRequired,
  scores: PropTypes.arrayOf(scoreProps).isRequired,
  thirdPartyScores: PropTypes.arrayOf(thirdPartyScoreProps).isRequired,
  type: PropTypes.string.isRequired,
  uuid: PropTypes.string,
};

const defaultProps = {
  uuid: undefined,
};

async function getData(type, dateOption) {
  try {
    const params = {
      date_type: dateOption.type,
      end_date: dateOption.endDate,
      start_date: dateOption.startDate,
      type,
    };
    const headers = { 'X-CSRF-Token': getAuthenticityToken() };

    const response = await fetch(scoreRecordsReportingReportsPath(params), { headers });
    if (!response.ok) {
      return { error: response.status };
    }

    return { data: await response.json() };
  } catch {
    return { error: 500 };
  }
}

function ScoreReport({
  attributeLookups,
  canViewSpend,
  customFilters,
  customRangeProps,
  customSegments,
  dateOptions,
  initialDescription,
  initialProperties,
  initialTitle,
  type,
  uuid,
  scores,
  thirdPartyScores,
}) {
  const [selectedDateRange, setSelectedDateRange] = useState(initialDateRange());
  const [selectedDateOption, setSelectedDateOption] = useState();
  const [rawRecords, setRawRecords] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedChartMetrics, setSelectedChartMetrics] = useState([]);
  const [selectedColCount, setSelectedColCount] = useState(columnCounts[0]);
  const [selectedVizType, setSelectedVizType] = useState(visualizationTypes[0]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filteredRecords, setFilteredRecords] = useState(records);
  const [headers, setHeaders] = useState([]);
  const [items, setItems] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [sort, setSort] = useState();
  const [sortedItems, setSortedItems] = useState([]);
  const [page, setPage] = useState(1);
  const [useCustomDates, setUseCustomDates] = useState(false);
  const filterCount = Object.keys(selectedFilters).length;
  const segmentsWithoutGroups = segments.filter(({ group }) => !group);

  const propertiesJson = calcPropertiesJson({
    selectedChartMetrics,
    selectedColCount,
    selectedDateOption: useCustomDates ? selectedDateRange : selectedDateOption,
    selectedFilters,
    selectedMetrics,
    selectedSegments,
    selectedVizType,
    sort,
  });
  const hasChanged = propertiesJson !== JSON.stringify(initialProperties);

  useEffect(() => {
    setSegments(getScoresSegments(scores, customFilters, customSegments, thirdPartyScores));
    setMetrics(getMetrics(canViewSpend, scores, thirdPartyScores));
  }, [scores, customFilters]);

  useEffect(() => {
    if (metrics.length > 0) {
      const parsed = parseProperties(initialProperties, {
        columnCounts,
        customRangeProps,
        dateOptions,
        metrics,
        segments,
        visualizationTypes,
      });

      setSelectedChartMetrics(parsed.selectedChartMetrics);
      setSelectedColCount(parsed.selectedColCount);
      setSelectedDateOption(parsed.selectedDateOption);
      setSelectedDateRange(parsed.selectedDateRange);
      setUseCustomDates(parsed.useCustomDates);
      setSelectedFilters(parsed.selectedFilters);
      setSelectedMetrics(parsed.selectedMetrics);
      setSelectedSegments(parsed.selectedSegments);
      setSelectedVizType(parsed.selectedVizType);
      setSort(parsed.sort);
    }
  }, [initialProperties, dateOptions, segments, metrics]);

  const getInflightData = () => {
    if (useCustomDates) {
      return getCustomData(
        selectedDateRange,
        scoreRecordsReportingReportsPath,
        scoreGroupingDimensionsReportingReportsPath,
        groupRecords,
      );
    }

    return getData('inflight', selectedDateOption);
  };

  useEffect(() => {
    (async () => {
      if (useCustomDates) mixpanelDateChange(selectedDateRange, 'score');

      const dateOption = useCustomDates ? selectedDateRange : selectedDateOption;
      if (dateOption) {
        setLoading(true);
        const responses = await Promise.all([
          getInflightData(),
          getData('preflight', dateOption),
        ]);

        if (responses.some((resp) => resp.error)) {
          const hasTimeout = responses.some((resp) => resp.error === 504);
          const message = hasTimeout ? 'The data request has timed out' : 'The data could not be loaded';

          addToast(message, { type: 'error' });
          setRawRecords([]);
        } else {
          setRawRecords(responses.reduce((all, resp) => [...all, ...resp.data], []));
        }
        setLoading(false);
      }
    })();
  }, [selectedDateOption, selectedDateRange]);

  useEffect(() => {
    setRecords(
      preprocessRecords(
        rawRecords,
        scores,
        customFilters,
        attributeLookups,
        thirdPartyScores,
      ),
    );
  }, [rawRecords, scores, customFilters, attributeLookups]);

  useEffect(() => {
    const filterTest = getObjFilterTest(selectedFilters);

    setFilteredRecords(records.filter(filterTest));
    setPage(1);
  }, [records, selectedFilters]);

  useEffect(() => {
    setCalculating(true);
    // @note: timeout lets component render with calculating true before calculation
    const timerId = setTimeout(() => {
      const bins = getBinsByTag(selectedSegments, filteredRecords);
      const allItems = getBinItems(bins, selectedSegments, scores, thirdPartyScores);
      setCalculating(false);

      setPage(1);
      setHeaders(getHeaders(selectedSegments, selectedMetrics, selectedChartMetrics));
      setItems(allItems);
      setTableStyles(selectedSegments);
    }, 10);

    return () => clearTimeout(timerId);
  }, [filteredRecords, scores, selectedChartMetrics, selectedMetrics, selectedSegments]);

  useEffect(() => {
    if (sort) {
      const byKeyDir = getItemSortBy(sort.key, sort.asc);
      const sorted = items.slice().sort(byKeyDir);
      const indexed = sorted.map((item, index) => ({
        ...item,
        index: { value: index + 1 },
      }));

      setSortedItems(indexed);
    }
  }, [items, sort]);

  const chartItems = useMemo(() => (
    sortedItems.slice(0, selectedColCount.value)
  ), [selectedColCount, sortedItems]);

  const updateSelectedMetrics = (selected) => {
    setSelectedChartMetrics((prev) => updateChartMetrics(prev, selected));
    setSelectedMetrics(selected);
  };

  const removeSelectedFilter = (key) => {
    setSelectedFilters((last) => removeProperty(last, key));
  };

  const handleSave = (reportUuid) => {
    window.location.href = editReportingReportPath(reportUuid);
  };

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);

    track('apply_filter', {
      filters,
      type,
    });
  };

  const handleDateRangeChange = (date, dateLabel) => {
    setSelectedDateRange((prev) => (
      {
        ...prev,
        [dateLabel]: date,
      }
    ));
  };

  const handleDateChange = (date) => {
    setSelectedDateOption(date);

    track('apply_date_change', {
      date: date.label,
      type,
    });
  };

  const handleVizTypeChange = (vizType) => {
    setSelectedVizType(vizType);

    track('apply_viz_type_change', {
      type,
      vizType: vizType.value,
    });
  };

  const getEmptyStateMessage = () => {
    if (loading) {
      return '';
    }
    if (calculating) {
      return 'Calculating metrics';
    }
    if (records.length === 0) {
      return 'No data to display';
    }
    if (sortedItems.length === 0 && filterCount !== 0) {
      return 'Remove filters to see data';
    }
    return null;
  };

  const renderTable = () => {
    const message = getEmptyStateMessage();

    if (message !== null) {
      return (
        <div className={`t-empty ${styles.empty}`}>
          { message }
        </div>
      );
    }

    return (
      <ItemsTable
        headers={headers}
        items={sortedItems}
        page={page}
        sort={sort}
        onPageChange={(value) => setPage(value)}
        onSortChange={setSort}
      />
    );
  };

  return (
    <>
      <ConfigureReport
        hasChanged={hasChanged}
        initialDescription={initialDescription}
        initialTitle={initialTitle}
        propertiesJson={propertiesJson}
        type={type}
        uuid={uuid}
        onExportCsv={(title) => saveItemsCsvFile(title, sortedItems, headers)}
        onSave={handleSave}
      />
      <Card padding={false}>
        <div className={styles.top}>
          <div className="u-flexRow u-justifyBetween u-alignEnd">
            <div className="u-flexRow u-gap-16">
              <Button
                label="Setup"
                variant="secondary"
                onClick={() => setDrawerOpen(true)}
              />
              <ReportingFilter
                isOpen={filterOpen}
                records={records}
                segments={segmentsWithoutGroups}
                selections={selectedFilters}
                onClose={() => setFilterOpen(false)}
                onOpen={() => setFilterOpen(true)}
                onSelectionsChange={handleFiltersChange}
              />
            </div>
            <div className="u-flexRow u-gap-16">
              <ReportDatePicker
                customRangeProps={customRangeProps}
                dateOptions={dateOptions}
                handleDateChange={handleDateChange}
                handleDateRangeChange={handleDateRangeChange}
                loading={loading}
                selectedDateOption={selectedDateOption}
                selectedDateRange={selectedDateRange}
                setUseCustomDates={setUseCustomDates}
                useCustomDates={useCustomDates}
              />
              <Dropdown
                disabled={loading}
                menuProps={{ size: 'small' }}
                options={visualizationTypes}
                selected={selectedVizType}
                size="small"
                onChange={handleVizTypeChange}
              />
            </div>
          </div>
          <ReportTags
            filters={selectedFilters}
            removeFilter={removeSelectedFilter}
            segments={segmentsWithoutGroups}
            onFilterClick={() => setFilterOpen(true)}
          />
        </div>
        <Divider />
        <MetricVisualization
          displayMetrics={selectedChartMetrics}
          items={chartItems}
          loading={loading || calculating}
          selectedSegments={selectedSegments}
          vizType={selectedVizType}
          multiple
        />
        <Divider />
        <div className={styles.table}>
          { renderTable() }
        </div>
      </Card>
      <BreakdownDrawer
        colCounts={columnCounts}
        isOpen={drawerOpen}
        metrics={metrics}
        segments={segments}
        selectedChartMetrics={selectedChartMetrics}
        selectedColCount={selectedColCount}
        selectedMetrics={selectedMetrics}
        selectedSegments={selectedSegments}
        selectedVizType={selectedVizType}
        setSelectedChartMetrics={setSelectedChartMetrics}
        setSelectedColCount={setSelectedColCount}
        setSelectedMetrics={updateSelectedMetrics}
        setSelectedSegments={setSelectedSegments}
        setSelectedVizType={setSelectedVizType}
        vizTypes={visualizationTypes}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
}

ScoreReport.propTypes = propTypes;
ScoreReport.defaultProps = defaultProps;

export default ScoreReport;
